"use client";

import React, { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useConsent } from '@/contexts/ConsentContext';
import { initTelemetry, shutdownTelemetry, track } from '@/lib/telemetry/posthog';
import { useTheme } from '@/contexts/ThemeContext';

// Watches consent.A and turns PostHog on/off. Fires app_opened once per
// session and page_viewed on every route change while telemetry is active.
// No-op when consent.A is false; SDK never imported in that case.
export const TelemetryGate: React.FC = () => {
  const { consent, loaded } = useConsent();
  const { theme } = useTheme();
  const pathname = usePathname();
  const startedRef = useRef(false);
  const lastRouteRef = useRef<string | null>(null);
  const themeRef = useRef(theme);

  // Keep latest theme available without re-firing the lifecycle effect on toggle.
  useEffect(() => {
    themeRef.current = theme;
  }, [theme]);

  // Lifecycle: start on consent grant, stop on revoke.
  useEffect(() => {
    if (!loaded) return;

    if (consent.A && !startedRef.current) {
      startedRef.current = true;
      initTelemetry().then((ph) => {
        if (!ph) return;
        const isPwa =
          typeof window !== 'undefined' &&
          (window.matchMedia?.('(display-mode: standalone)')?.matches ||
            // iOS Safari quirk
            (window.navigator as Navigator & { standalone?: boolean }).standalone === true);
        track('app_opened', {
          locale: typeof document !== 'undefined' ? document.documentElement.lang || 'en' : 'en',
          theme: themeRef.current,
          is_pwa: Boolean(isPwa),
        });
      });
    } else if (!consent.A && startedRef.current) {
      startedRef.current = false;
      shutdownTelemetry();
    }
  }, [consent.A, loaded]);

  // Route change → page_viewed. Only emits while telemetry started.
  useEffect(() => {
    if (!startedRef.current || !pathname) return;
    if (lastRouteRef.current === pathname) return;
    lastRouteRef.current = pathname;
    track('page_viewed', { route: pathname });
  }, [pathname]);

  return null;
};
